import {computed, ref, watch} from "@vue/composition-api";
import {useToast} from "vue-toastification/composition";
import ToastificationContent
  from "@core/components/toastification/ToastificationContent.vue";
import moment from "moment-timezone";
import store from "@/store";
import ability from "@/libs/acl/ability";
import useFilterCounter from "@/components/Filters/useFiltersCount";
import {debounce} from "lodash";
import axios from "@axios";
import {decryptData} from "@/utils/decryptUserDataUtils";

export default function useList(showFilters = true, initialFilters = null) {
  const refListTable = ref(null);

  const toast = useToast();

  const canBankSetting = ability.can("bank_setting", "Units")

  const tableColumns = [
    { label: "unit.description", key: "description", sortable: true },
    { label: "unit.sector", key: "sector.name", sortable: true },
    { label: "unit.is_disabled", key: "deactivation", sortable: false },
    ...(canBankSetting ? [{ label: "unit.is_main_unit", key: "is_main_unit", sortable: false }] : []),
    { label: "unit.category", key: "category.name", sortable: false },
    { label: "unit.camera_count", key: "cameras.length", sortable: false },
    { label: "country.title", key: "country.name", sortable: false },
    { label: "region.title", key: "region.name", sortable: true },
    { label: "municipality.title", key: "municipality.name", sortable: true },
    { label: "unit.timeZone", key: "time_zone", sortable: true },
    { label: "router.latitude", key: "router.lat", sortable: false },
    { label: "router.longitude", key: "router.lng", sortable: false },
    { label: "unit.address_1", key: "address_1", sortable: false },
    { label: "unit.address_2", key: "address_2", sortable: false },
    { label: "unit.postal_code", key: "postal_code", sortable: true },
  ];

  if (showFilters && (ability.can("update", "Units") || ability.can("delete", "Unit"))) {
    tableColumns.splice(0, 0, { label: "actions.name", key: "actions" });
  }

  if (ability.can("advance_setting", "Units")) {
    const offset =
      showFilters && (ability.can("update", "Unit") || ability.can("delete", "Unit"))
        ? 1
        : 0;
    tableColumns.splice(2 + offset, 0, {
      label: "router.imei",
      key: "router.imei",
      sortable: false,
      tdClass: "text-truncate router",
      thStyle: "max-width: 150px;",
    });
    tableColumns.splice(7 + offset, 0, {
      label: "router.ip_vpn",
      key: "router.ip_vpn",
      sortable: false,
    });
    tableColumns.splice(11 + offset, 0, {
      label: "router.notify_cad_c5_cdmx",
      key: "router.notify_c5_cdmx",
      sortable: true,
    });
    tableColumns.splice(12 + offset, 0, {
      label: "router.notify_cad_c5_jalisco",
      key: "router.notify_c5_jal",
      sortable: true,
    });
    tableColumns.splice(14 + offset, 0, {
      label: "unit.exterior_number",
      key: "exterior_number",
      sortable: false,
    });
    tableColumns.splice(15 + offset, 0, {
      label: "unit.suburb",
      key: "suburb",
      sortable: true,
    });
    tableColumns.splice(16 + offset, 0, {
      label: "unit.number",
      key: "number",
      sortable: false,
    });
    tableColumns.splice(18 + offset, 0, {
      label: "unit.route",
      key: "route",
      sortable: false,
    });
    tableColumns.splice(19 + offset, 0, {
      label: "unit.plate_number",
      key: "plate_number",
      sortable: true,
    });
    tableColumns.splice(20 + offset, 0, {
      label: "unit.vin",
      key: "vin",
      sortable: true,
    });
    tableColumns.splice(21 + offset, 0, {
      label: "unit.driver",
      key: "driver",
      sortable: true,
    });
    tableColumns.splice(22 + offset, 0, {
      label: "unit.provider",
      key: "provider",
      sortable: true,
    });
    tableColumns.splice(23 + offset, 0, {
      label: "unit.company",
      key: "owner",
      sortable: true,
    });
    tableColumns.splice(24 + offset, 0, {
      label: "unit.vehicle",
      key: "vehicle",
      sortable: true,
    });
    tableColumns.splice(25 + offset, 0, {
      label: "unit.allow_virtual_button_alarm",
      key: "allow_virtual_button_alarm",
      sortable: true,
    });
    tableColumns.splice(26 + offset, 0, {
      label: "unit.active_digital_output",
      key: "active_digital_output",
      sortable: true,
    });
  }

  if (ability.can("bank_setting", "Units")) {
    const offset =
      showFilters && (ability.can("update", "Unit") || ability.can("delete", "Unit"))
        ? 1
        : 0;
    tableColumns.splice(2 + offset, 0, {
      label: "unit.bank_data",
      key: "rems",
      sortable: false,
    });
  }

  const user = JSON.parse(decryptData(window.localStorage.userData));

  if (user.is_master_admin) {
    tableColumns.splice(1, 0, { label: "id_router", key: "id_router", sortable: false });
  }

  const perPage = ref(10);
  const totalList = ref(0);
  const currentPage = ref(1);
  const perPageOptions = [10, 25, 50, 100];
  const searchQuery = ref("");
  const sortBy = ref("id");
  const isSortDirDesc = ref(true);

  const onlineFilter = ref(null);
  const sendC5CdmxFilter = ref(null);
  const sendC5JalFilter = ref(null);
  const facetedFilters = ref({});

  const dataMeta = computed(() => {
    const localItemsCount = refListTable.value ? refListTable.value.localItems.length : 0;
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalList.value,
    };
  });

  store.dispatch("facility_category/getAll");
  const categoryOptions = computed(
    () => store.getters["facility_category/getSelectedItems"]
  );
  const categoryFilter = ref(initialFilters?.category ?? null);

  store.dispatch("unit_deactivation_catalog/getAll");
  const unitDeactivationCatalogOptions = computed(
    () => store.getters["unit_deactivation_catalog/getSelectedItems"]
  );

  store.dispatch("tenant/getAll");
  const tenantOptions = computed(() => store.getters["tenant/getSelectedItems"]);
  const tenantFilter = ref(null);

  store.dispatch("country/getAll");
  const countryOptions = computed(() => store.getters["country/getSelectedItems"]);
  const countryFilter = ref(null);

  store.dispatch("region/getAll");
  const regionOptions = computed(() => store.getters["region/getSelectedItems"]);
  const regionFilter = ref(null);

  store.dispatch("municipality/getAll");
  const municipalityOptions = computed(
    () => store.getters["municipality/getSelectedItems"]
  );
  const municipalityFilter = ref(null);

  store.dispatch("sector/getAll");
  const sectorOptions = computed(() => store.getters["sector/getSelectedItems"]);

  store.dispatch("center/getAll");
  const centerOptions = computed(() => store.getters["center/getSelectedItems"]);

  const userOptions = computed(() => store.getters["user/getSelectedItems"]);

  const enabledNearbyLocationsFilter = ref(null);
  const distanceFilter = ref(null);

  const timezoneOptions = ref([]);

  const fetchUsers = async (query = "") => {
    await store.dispatch("user/getAll", { max: 10, q: query });
  };

  const formTemplateOptionsForAssignTask = ref([]);

  const fetchFormTemplatesForAssignTask = () => {
    axios
      .get("/v1/operational-security-forms/templates", {
        params: {
          q: "",
          max: 500,
          "page[n]": 1,
          sortBy: "name",
          sortDesc: false,
        },
      })
      .then((response) => {
        const list = response.data && response.data.data ? response.data.data : [];
        const byId = new Map();
        list.forEach((item) => {
          if (item.id != null && !byId.has(item.id)) {
            byId.set(item.id, { value: item.id, label: item.name || String(item.id) });
          }
        });
        formTemplateOptionsForAssignTask.value = Array.from(byId.values());
      })
      .catch(() => {});
  };

  fetchFormTemplatesForAssignTask();

  const fecthTimezeones = async () => {
    const customTimezones = axios.get("v1/custom-timezones", {
      params: { "page[n]": 1 },
    });
    const timezones = axios.get("v1/timezone");

    Promise.all([customTimezones, timezones])
      .then((response) => {
        const [customTimezonesResponse, timezonesResponse] = response;
        const { data: dataCustomTimezones } = customTimezonesResponse;
        const { data: dataTimezones } = timezonesResponse;

        if (dataCustomTimezones.data.length) {
          const timezonesCustom = dataCustomTimezones.data.map((ctz) => ({
            name: `${ctz.name} DTS`,
          }));

          timezoneOptions.value = timezonesCustom;
        }

        const timezones = dataTimezones.data.map((tz) => ({
          name: tz.name,
        }));

        timezoneOptions.value = [...timezoneOptions.value, ...timezones];
      })
      .catch(() => {
        timezoneOptions.value = [];
      });
  };

  fecthTimezeones();

  const fetchList = (ctx, callback) => {
    store
      .dispatch("unit/getAll", {
        q: searchQuery.value,
        max: perPage.value,
        "page[n]": currentPage.value,
        sortBy: sortBy.value,
        sortDesc: isSortDirDesc.value,
        tenant: tenantFilter.value,
        country: countryFilter.value,
        region: regionFilter.value,
        municipality: municipalityFilter.value,
        online: onlineFilter.value,
        send_c5_cdmx: sendC5CdmxFilter.value,
        send_c5_jal: sendC5JalFilter.value,
        category: categoryFilter.value,
        near_location: enabledNearbyLocationsFilter.value,
        distance: distanceFilter.value,
        ...facetedFilters.value,
        with: [
          "router",
          "router.tenant",
          "country",
          "region",
          "municipality",
          "cameras",
          "category",
          "additional_info",
          "lastRiskEvaluation",
        ],
      })
      .then((response) => {
        callback(response.data);
        totalList.value = response.meta.total;
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: "Error obteniendo la información",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      });
  };

  const formatDateAssigned = (value) => {
    const formattedDate = moment(value).format("DD/MM/YYYY HH:mm:ss");
    return formattedDate;
  };

  const refetchData = () => {
    refListTable.value.refresh();
  };

  watch([currentPage, perPage], () => {
    refetchData();
  });

  watch(
    [searchQuery],
    debounce(() => {
      refetchData();
    }, 500)
  );

  // filters counter ============================
  const filters = ref({});
  const filtersCount = useFilterCounter(filters);
  const buildFilters = () => {
    filters.value = {
      tenants: tenantFilter.value,
      countries: countryFilter.value,
      regions: regionFilter.value,
      municipalities: municipalityFilter.value,
      category: categoryFilter.value,
      online: onlineFilter.value,
      send_c5_cdmx: sendC5CdmxFilter.value,
      send_c5_jal: sendC5JalFilter.value,
      near_location: enabledNearbyLocationsFilter.value,
      distance: distanceFilter.value,
    };
    refetchData();
  };
  const clearSearchFilters = () => {
    tenantFilter.value = null;
    countryFilter.value = null;
    regionFilter.value = null;
    municipalityFilter.value = null;
    onlineFilter.value = null;
    sendC5CdmxFilter.value = null;
    sendC5JalFilter.value = null;
    enabledNearbyLocationsFilter.value = null;
    distanceFilter.value = null;
    categoryFilter.value = null;
    filters.value = {};
    buildFilters();
  };
  const setFacetedFilters = (filters) => {
    facetedFilters.value = {...filters, ...{advanced_search: true}}
    refetchData();
  };
  // ============================================

  return {
    tableColumns,
    perPage,
    currentPage,
    totalList,
    fetchList,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refListTable,
    formatDateAssigned,
    categoryOptions,
    categoryFilter,
    unitDeactivationCatalogOptions,
    tenantOptions,
    tenantFilter,
    onlineFilter,
    regionOptions,
    regionFilter,
    countryOptions,
    countryFilter,
    municipalityOptions,
    municipalityFilter,
    sendC5CdmxFilter,
    sendC5JalFilter,
    enabledNearbyLocationsFilter,
    distanceFilter,
    sectorOptions,
    centerOptions,
    userOptions,
    timezoneOptions,

    refetchData,

    filtersCount,
    clearSearchFilters,
    buildFilters,
    fetchUsers,
    formTemplateOptionsForAssignTask,
    fetchFormTemplatesForAssignTask,
    setFacetedFilters,
  };
}
